import axiosInstance from '../utils/axiosConfig';

/**
 * Leave Service
 * Handles all leave-related API calls
 */

const leaveService = {
  /**
   * Get list of available users (not on leave) for work assignment
   * @param {string} startDate - Optional start date (YYYY-MM-DD) to check for overlapping leave
   * @param {string} endDate - Optional end date (YYYY-MM-DD) to check for overlapping leave
   * @param {number} excludeEmployeeId - Optional employee ID to exclude (e.g., the person requesting leave)
   * @returns {Promise<Array>} Array of available users
   */
  getAvailableUsers: async (startDate = null, endDate = null, excludeEmployeeId = null) => {
    try {
      let url = '/api/leave/available-users/';
      const params = new URLSearchParams();
      
      // Add date parameters if provided
      if (startDate && endDate) {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      
      // Add exclude employee ID if provided
      if (excludeEmployeeId) {
        params.append('exclude_employee_id', excludeEmployeeId);
      }
      
      // Build URL with parameters
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axiosInstance.get(url);
      return response.data.available_users || [];
    } catch (error) {
      console.error('Error fetching available users:', error);
      throw error;
    }
  },

  /**
   * Approve a leave request with work assignment
   * @param {number} leaveRequestId - ID of the leave request
   * @param {number|null} assignedUserId - ID of user to assign work to (optional for HO/TL)
   * @param {string} adminNotes - Optional admin notes
   * @returns {Promise<Object>} Approval response
   */
  approveLeaveRequest: async (leaveRequestId, assignedUserId, adminNotes = '') => {
    try {
      const payload = {
        admin_notes: adminNotes
      };
      
      // Only include assigned_user_id if provided (not required for HO/TL)
      if (assignedUserId) {
        payload.assigned_user_id = assignedUserId;
      }
      
      const response = await axiosInstance.post(
        `/api/leave/admin/requests/${leaveRequestId}/approve/`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Error approving leave request:', error);
      throw error;
    }
  },

  /**
   * Reject a leave request
   * @param {number} leaveRequestId - ID of the leave request
   * @param {string} adminNotes - Optional admin notes
   * @returns {Promise<Object>} Rejection response
   */
  rejectLeaveRequest: async (leaveRequestId, adminNotes = '') => {
    try {
      const response = await axiosInstance.post(
        `/api/leave/admin/requests/${leaveRequestId}/reject/`,
        {
          admin_notes: adminNotes
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error rejecting leave request:', error);
      throw error;
    }
  },

  /**
   * Assign work coverage for an already-approved leave (notify-only types)
   * @param {number} leaveRequestId - ID of the leave request
   * @param {number} assignedUserId - ID of user to assign work to
   * @returns {Promise<Object>} Assignment response
   */
  assignLeaveWork: async (leaveRequestId, assignedUserId) => {
    try {
      const response = await axiosInstance.post(
        `/api/leave/requests/${leaveRequestId}/assign-work/`,
        {
          assigned_user_id: assignedUserId
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error assigning leave work:', error);
      throw error;
    }
  },

  /**
   * Edit an existing leave request (dates, day types, and reason only)
   * @param {number} leaveRequestId - ID of the leave request to edit
   * @param {Object} editData - Edited data
   * @param {string} editData.start_date - New start date (YYYY-MM-DD) - optional
   * @param {string} editData.end_date - New end date (YYYY-MM-DD) - optional
   * @param {string} editData.start_day_type - 'full', 'morning', or 'afternoon' - optional
   * @param {string} editData.end_day_type - 'full', 'morning', or 'afternoon' - optional
   * @param {string} editData.reason - New reason text - optional
   * @returns {Promise<Object>} Edit response with updated leave request
   */
  editLeaveRequest: async (leaveRequestId, editData) => {
    try {
      const response = await axiosInstance.put(
        `/api/leave/requests/${leaveRequestId}/edit/`,
        editData
      );
      return response.data;
    } catch (error) {
      console.error('Error editing leave request:', error);
      throw error;
    }
  },

  /**
   * Get list of users who are on approved leave during a specified period
   * @param {string} startDate - Start date (YYYY-MM-DD) to check for overlapping leave
   * @param {string} endDate - End date (YYYY-MM-DD) to check for overlapping leave
   * @param {number} excludeEmployeeId - Optional employee ID to exclude (e.g., the person requesting leave)
   * @returns {Promise<Array>} Array of users on leave with name, leave type, dates, and color
   */
  getUsersOnLeave: async (startDate, endDate, excludeEmployeeId = null) => {
    try {
      const params = new URLSearchParams();
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      
      // Add exclude employee ID if provided
      if (excludeEmployeeId) {
        params.append('exclude_employee_id', excludeEmployeeId);
      }
      
      const response = await axiosInstance.get(`/api/leave/users-on-leave/?${params.toString()}`);
      return response.data.users_on_leave || [];
    } catch (error) {
      console.error('Error fetching users on leave:', error);
      throw error;
    }
  },

  /**
   * Calculate consecutive sick leave days including adjacent requests
   * @param {string} startDate - Start date of the new/current request (YYYY-MM-DD)
   * @param {string} endDate - End date of the new/current request (YYYY-MM-DD)
   * @param {number} excludeRequestId - Optional ID of request to exclude (for edits)
   * @returns {Promise<Object>} Object with consecutive_days, requires_sick_note, and adjacent_request_ids
   */
  calculateConsecutiveSickDays: async (startDate, endDate, excludeRequestId = null) => {
    try {
      const payload = {
        start_date: startDate,
        end_date: endDate
      };
      
      if (excludeRequestId) {
        payload.exclude_request_id = excludeRequestId;
      }
      
      const response = await axiosInstance.post('/api/leave/calculate-consecutive-sick-days/', payload);
      return response.data;
    } catch (error) {
      console.error('Error calculating consecutive sick days:', error);
      throw error;
    }
  }
};

export default leaveService;

